import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import Select2Field from "components/Select2Field";
import { useTranslate } from "utils/i18n";
import { VIEW_MODES, getAvailableViewModes, MAP_CONTEXT } from "./mapHelpers";

const RADIUS_PRESETS = [250, 500, 1000, 2000];

function FilterSection({ title, hint, children }) {
  return (
    <section className="map-drawer-section">
      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.25, color: "#334155" }}>
        {title}
      </SoftTypography>
      {hint ? (
        <SoftTypography variant="caption" sx={{ display: "block", mb: 1.25, color: "#64748b", lineHeight: 1.45 }}>
          {hint}
        </SoftTypography>
      ) : (
        <SoftBox mb={1.25} />
      )}
      {children}
    </section>
  );
}

FilterSection.propTypes = {
  title: PropTypes.string.isRequired,
  hint: PropTypes.string,
  children: PropTypes.node.isRequired,
};

FilterSection.defaultProps = {
  hint: "",
};

export default function MapFiltersDrawer({
  open,
  onClose,
  values,
  onChange,
  onReset,
  advancedFilterCount,
  genderOptions,
  degreeOptions,
  contractTypeOptions,
  typeOptions,
  statusOptions,
  filteredCount,
  totalCount,
  mapContext,
  checkInCount,
}) {
  const { t } = useTranslate();
  const viewModeOptions = getAvailableViewModes(mapContext || MAP_CONTEXT.IDLE, checkInCount || 0);

  const field = (label, name, options, value) => (
    <div className="map-drawer-field">
      <label className="map-drawer-label">{label}</label>
      <Select2Field
        name={name}
        options={options}
        placeholder={label}
        value={value}
        onChange={(val) => onChange(name, val)}
        allowClear={false}
        dropdownParent={typeof document !== "undefined" ? document.body : undefined}
      />
    </div>
  );

  const activePills = [];
  if (values.viewMode && values.viewMode !== "markers") {
    const mode = VIEW_MODES.find((m) => m.value === values.viewMode);
    activePills.push(t(mode?.labelKey || "map.view.markers", mode?.fallback || values.viewMode));
  }
  if (values.radiusMeters && String(values.radiusMeters) !== "500") {
    activePills.push(`${values.radiusMeters} m`);
  }
  if (values.radiusFilter === "in") activePills.push(t("map.kpi.in_radius", "In radius"));
  if (values.radiusFilter === "out") activePills.push(t("map.kpi.out_radius", "Out of radius"));
  if (values.gender) activePills.push(values.gender);
  if (values.degree) activePills.push(values.degree);
  if (values.contractType) activePills.push(values.contractType);
  if (values.type) activePills.push(typeOptions.find((option) => option.id === values.type)?.text || values.type);
  if (values.status) {
    activePills.push(statusOptions.find((option) => option.id === values.status)?.text || values.status);
  }
  if (values.compareDate) activePills.push(`${t("map.filter.compare_date", "Compare")}: ${values.compareDate}`);
  if (values.estabHeatLayer) activePills.push(t("map.filter.estab_heat", "Sub-prefecture heat"));

  return (
    <>
      <button
        type="button"
        className={`map-filters-drawer-backdrop${open ? " map-filters-drawer-backdrop--open" : ""}`}
        onClick={onClose}
        aria-hidden={!open}
        tabIndex={open ? 0 : -1}
      />

      <aside className={`map-filters-drawer${open ? " map-filters-drawer--open" : ""}`} aria-hidden={!open}>
        <div className="map-drawer-header">
          <SoftBox>
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#0f172a" }}>
              {t("map.drawer.title", "Map filters")}
            </SoftTypography>
            <SoftTypography variant="caption" sx={{ color: "#64748b" }}>
              {t("map.drawer.subtitle", "Refine what appears on the map without leaving the view.")}
            </SoftTypography>
          </SoftBox>
          <button type="button" className="map-drawer-close" onClick={onClose} aria-label={t("map.drawer.close", "Close")}>
            <Icon sx={{ fontSize: "1.2rem !important" }}>close</Icon>
          </button>
        </div>

        {totalCount > 0 && (
          <div className="map-drawer-summary">
            <strong>{filteredCount}</strong>
            <span>
              {t("map.drawer.results_of", "of {{total}} check-in(s) shown").replace("{{total}}", String(totalCount))}
            </span>
          </div>
        )}

        {activePills.length > 0 && (
          <div className="map-drawer-pills">
            {activePills.map((pill) => (
              <span key={pill} className="map-drawer-pill">
                {pill}
              </span>
            ))}
          </div>
        )}

        <div className="map-drawer-body">
          <FilterSection
            title={t("map.drawer.section_display", "Map display")}
            hint={t("map.drawer.section_display_hint", "Choose how teacher locations are drawn.")}
          >
            {field(
              t("map.filter.view_mode", "View"),
              "viewMode",
              viewModeOptions.map((mode) => ({
                id: mode.value,
                text: t(mode.labelKey, mode.fallback),
              })),
              values.viewMode
            )}
            <SoftTypography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5, lineHeight: 1.45 }}>
              {mapContext === MAP_CONTEXT.TEACHER_TIMELINE
                ? t("map.drawer.view_context_teacher", "Lines show this teacher's check-ins to the school. Heatmap uses this teacher only.")
                : mapContext === MAP_CONTEXT.SCHOOL_OVERVIEW
                  ? t("map.drawer.view_context_school", "Lines link the school to each teacher check-in. Heatmap uses all teachers shown.")
                  : t("map.drawer.view_context_area", "Overview of all schools in the sub-prefecture.")}
            </SoftTypography>
            <div className="map-drawer-view-hints">
              {viewModeOptions.map((mode) => {
                const hintDefaults = {
                  markers: "One dot per teacher, colored by distance from school.",
                  cluster: "Groups nearby teachers with a count badge.",
                  heatmap: "Colored zones showing check-in density.",
                  lines: "Draws a line from school to each teacher check-in.",
                };
                return (
                  <p key={mode.value} className={values.viewMode === mode.value ? "is-active" : ""}>
                    <strong>{t(mode.labelKey, mode.fallback)}</strong>
                    {" — "}
                    {t(`map.drawer.view_hint.${mode.value}`, hintDefaults[mode.value] || mode.fallback)}
                  </p>
                );
              })}
            </div>
          </FilterSection>

          <FilterSection
            title={t("map.drawer.section_distance", "Distance & presence")}
            hint={t("map.drawer.section_distance_hint", "Define how close a check-in must be to count as at school.")}
          >
            <label className="map-drawer-label">{t("map.filter.radius", "Radius (m)")}</label>
            <SoftInput
              type="number"
              min={1}
              name="radiusMeters"
              value={values.radiusMeters}
              onChange={(e) => onChange("radiusMeters", e.target.value)}
              className="map-toolbar-input"
              sx={{ mb: 1 }}
            />
            <div className="map-drawer-presets">
              {RADIUS_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`map-drawer-preset${String(values.radiusMeters) === String(preset) ? " is-active" : ""}`}
                  onClick={() => onChange("radiusMeters", String(preset))}
                >
                  {preset} m
                </button>
              ))}
            </div>
            {field(
              t("map.filter.radius_filter", "Show teachers"),
              "radiusFilter",
              [
                { id: "", text: t("map.filter.radius_all", "All check-ins") },
                { id: "in", text: t("map.drawer.only_in_radius", "Only inside radius") },
                { id: "out", text: t("map.drawer.only_out_radius", "Only outside radius") },
              ],
              values.radiusFilter
            )}
          </FilterSection>

          <FilterSection
            title={t("map.drawer.section_compare", "Compare dates")}
            hint={t(
              "map.drawer.section_compare_hint",
              "Pick one extra day, then Search. The map switches to Markers automatically: green/orange = your From–To range, purple = compare day (dots are shifted so both are visible). Lines and heatmap are disabled during compare."
            )}
          >
            <label className="map-drawer-label" htmlFor="map-compare-date">
              {t("map.filter.compare_date", "Compare date")}
            </label>
            <input
              id="map-compare-date"
              type="date"
              name="compareDate"
              value={values.compareDate || ""}
              onChange={(e) => onChange("compareDate", e.target.value)}
              className="map-toolbar-input map-toolbar-native-date"
            />
          </FilterSection>

          <FilterSection
            title={t("map.drawer.section_area", "Sub-prefecture")}
            hint={t("map.drawer.section_area_hint", "Show establishment attendance density across the area.")}
          >
            <label className="map-drawer-label map-drawer-checkbox-row">
              <input
                type="checkbox"
                checked={Boolean(values.estabHeatLayer)}
                onChange={(e) => onChange("estabHeatLayer", e.target.checked)}
              />
              <span>{t("map.filter.estab_heat", "Sub-prefecture heat layer")}</span>
            </label>
          </FilterSection>

          <FilterSection
            title={t("map.drawer.section_establishment", "Establishment")}
            hint={t("map.drawer.section_establishment_hint", "Filter establishments by educational type and category.")}
          >
            {field(
              t("establishments.col.type", "Type"),
              "type",
              typeOptions,
              values.type
            )}
            {field(
              t("establishments.col.status", "Status"),
              "status",
              statusOptions,
              values.status
            )}
          </FilterSection>

          <FilterSection
            title={t("map.drawer.section_teacher", "Teacher profile")}
            hint={t("map.drawer.section_teacher_hint", "Filter by gender, contract type, or degree.")}
          >
            {field(
              t("home.table.gender", "Gender"),
              "gender",
              [{ id: "", text: t("map.filter.all_genders", "All genders") }, ...genderOptions],
              values.gender
            )}
            {field(
              t("teachers.table.contract_type", "Type de contrat"),
              "contractType",
              [{ id: "", text: t("map.filter.all_contract_types", "All contract types") }, ...contractTypeOptions],
              values.contractType
            )}
            {field(
              t("map.filter.degree", "Diplôme"),
              "degree",
              [{ id: "", text: t("map.filter.all_degrees", "All degrees") }, ...degreeOptions],
              values.degree
            )}
          </FilterSection>

          <FilterSection title={t("map.drawer.legend", "Legend")} hint="">
            <ul className="map-drawer-legend">
              <li>
                <span className="map-teacher-dot map-teacher-dot--in" aria-hidden="true" />
                {t("map.drawer.legend_in", "Green — check-in inside allowed radius")}
              </li>
              <li>
                <span className="map-teacher-dot map-teacher-dot--out" aria-hidden="true" />
                {t("map.drawer.legend_out", "Orange — check-in outside allowed radius")}
              </li>
              <li>
                <span className="map-teacher-dot map-teacher-dot--compare" aria-hidden="true" />
                {t("map.drawer.legend_compare", "Purple — compare date check-in")}
              </li>
              <li>
                <span className="map-drawer-legend-school map-drawer-legend-school--high" aria-hidden="true" />
                {t("map.attendance_high", "Higher attendance")}
              </li>
              <li>
                <span className="map-drawer-legend-school map-drawer-legend-school--low" aria-hidden="true" />
                {t("map.attendance_low", "Lower attendance")}
              </li>
            </ul>
          </FilterSection>
        </div>

        <div className="map-drawer-footer">
          <SoftButton
            type="button"
            variant="outlined"
            color="dark"
            fullWidth
            disabled={advancedFilterCount === 0}
            onClick={onReset}
            sx={{ mb: 1 }}
          >
            {t("map.filter.reset_advanced", "Reset filters")}
          </SoftButton>
          <SoftButton type="button" variant="gradient" color="info" fullWidth onClick={onClose}>
            {t("map.drawer.apply", "Done")}
          </SoftButton>
        </div>
      </aside>
    </>
  );
}

MapFiltersDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  advancedFilterCount: PropTypes.number.isRequired,
  genderOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  degreeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  contractTypeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  typeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  statusOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  filteredCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  mapContext: PropTypes.string,
  checkInCount: PropTypes.number,
};
